import Modal from "@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal";
import DeviceSearch from "./DeviceSearch/DeviceSearch";
import { DeviceSearchHeader } from "./DeviceSearch/DeviceSearchHeader/DeviceSearchHeader";
import styles from "./TradeInModal.module.scss";
import classNames from "classnames/bind";
import Grading from "./Grading/Grading";
import PaymentOptions from "./PaymentOptions/PaymentOptions";
import { useTradeInContext } from "../../TradeInContext";
import { dispatchMonetateQEvent } from "../../../../utils/monetate/monetateQ";
import { TradeInEvents } from "../../TradeIn";
import TradeInUnavailable from "./TradeInUnavailable/TradeInUnavailable";
import { useEffect, useState } from "react";
import { getBasketQuotes } from "./Confirmation/util/getBasketQuotes";
import Confirmation from "./Confirmation/Confirmation";
import { GradingHeader } from "./Grading/GradingHeader/GradingHeader";

const cx = classNames.bind(styles);

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onFetchQuotesComplete: () => void;
  setTradeInModelOpen: (isTradeInModelOpen: boolean) => void;
};

export enum TRADE_IN_STEPS {
  FIND_DEVICE,
  GRADING_QUESTIONS,
  PAYMENT_OPTIONS,
  CONFIRMATION,
  UNAVAILABLE,
}

const TradeInModal = ({
  isOpen,
  onClose,
  onFetchQuotesComplete,
  setTradeInModelOpen,
}: Props) => {
  const {
    electricalApiBaseUrl,
    currentScreen,
    selectedDevice,
    setCurrentScreen,
    setExistingQuotes,
    shouldCloseModal,
    resetContextData,
    getQuotesFromBasket,
    basketApiBaseUrl,
  } = useTradeInContext();
  const apiBaseUrl = getQuotesFromBasket
    ? basketApiBaseUrl
    : electricalApiBaseUrl;
  const [loading, setLoading] = useState<boolean>(false);

  const modalScreens = [
    DeviceSearch,
    Grading,
    PaymentOptions,
    Confirmation,
    TradeInUnavailable,
  ];
  const getActiveScreen = () => modalScreens[currentScreen];

  useEffect(() => {
    const specialOfferListener = () => {
      setTradeInModelOpen(true);
    };

    document.addEventListener(
      TradeInEvents.TRADE_IN_SPECIAL_OFFERS_DRAWER_EVENT,
      specialOfferListener,
    );

    return () => {
      document.removeEventListener(
        TradeInEvents.TRADE_IN_SPECIAL_OFFERS_DRAWER_EVENT,
        specialOfferListener,
      );
    };
  });
  const renderCurrentScreen = () => {
    const ActiveScreen = getActiveScreen();

    // @ts-ignore
    return <ActiveScreen />;
  };

  useEffect(() => {
    (async () => {
      const fetchInitialQuotes = async (): Promise<void> => {
        setLoading(true);
        const quotesResponses = await getBasketQuotes(
          apiBaseUrl,
          getQuotesFromBasket,
        );
        setExistingQuotes(quotesResponses);
        if (quotesResponses.length > 0) {
          setCurrentScreen(TRADE_IN_STEPS.CONFIRMATION);
        }
        setLoading(false);
        onFetchQuotesComplete();
      };
      if (isOpen) {
        await fetchInitialQuotes();
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    if (shouldCloseModal) {
      resetContextData();
      handleOnClose();
    }
  }, [shouldCloseModal]);

  if (loading) return;

  const modalClassNames = cx(styles.tradeInModal, {
    "tradeInModal--sand": [DeviceSearch, Grading].includes(
      // @ts-ignore
      getActiveScreen(),
    ),
  });
  const handleOnClose = () => {
    if (currentScreen === TRADE_IN_STEPS.FIND_DEVICE) {
      dispatchMonetateQEvent(TradeInEvents.PW_TRADE_IN_SEARCH_STEP_DROP_OUT);
    }

    resetContextData();
    onClose();
  };

  const displayBackButton = () =>
    [TRADE_IN_STEPS.GRADING_QUESTIONS, TRADE_IN_STEPS.PAYMENT_OPTIONS].includes(
      currentScreen,
    );

  function renderSubHeader() {
    if (currentScreen === TRADE_IN_STEPS.FIND_DEVICE) {
      return <DeviceSearchHeader />;
    }

    if (currentScreen === TRADE_IN_STEPS.GRADING_QUESTIONS) {
      return selectedDevice && <GradingHeader device={selectedDevice} />;
    }
    return null;
  }

  return (
    <Modal
      rootId="page-root"
      hasPadding={false}
      role="dialog"
      isOpen={isOpen}
      header={"Trade In"}
      onClose={handleOnClose}
      showBackButton={displayBackButton()}
      headerTitleClassName={
        displayBackButton()
          ? styles["tradeInModal__title--with-backBtn"]
          : styles["tradeInModal__title"]
      }
      modalChildrenClassName={styles.tradeInModal__children}
      headerChildren={renderSubHeader()}
      className={modalClassNames}
      headerStyleModifier={styles.tradeInModal__header}
      headerChildrenClassName={styles.tradeInModal__subHeader}
      onBack={() => setCurrentScreen(currentScreen - 1)}
    >
      <div className={styles.tradeInModal__content}>
        {renderCurrentScreen()}
      </div>
    </Modal>
  );
};

export default TradeInModal;
